/**
 * 取消した見積依頼書の削除の窓（#275、docs/99 §5、Figma 292:228。見積書のゴミ箱の「完全に削除」と同じ形）。
 *   題「削除」／「見積依頼書「RQ-0001」を完全に削除します。」／「番号 RQ-0001 は欠番になり、PDF と送付の控えも削除します。元に戻せません。」
 *   ／回答の{vendorQuote}があれば「回答の{vendorQuote} V-0003 は残ります（依頼との紐づけだけ外れます）。」／主は「取消」（はじめのフォーカス）・「削除」は朱。
 *   見積依頼書の画面・見積依頼の一覧・{project}のタブ「見積依頼」の3か所の「⋯」から開く（同じ窓）。
 *   削除の API（DELETE /rfqs/:id）は窓の中で呼び、断られたら窓の中に API の文（{order}の元になった依頼など）。
 *   通ったら通知「RQ-0001 を削除しました」。to（見積依頼書の画面＝{project}の見積依頼タブか一覧）があれば移って、移った先で通知（画面を移ると通知は消えるため state.notice）
 */
import { RFQ_MSG, type RfqSummary } from "@genbaryoku/contract";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, messageOf } from "../../api.js";
import { notify } from "../../components/Notice.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { t } from "../../labels.js";

export function RfqPurgeDialog({ rfq, onClose, to }: {
  rfq: Pick<RfqSummary, "id" | "no" | "answer">;
  onClose: () => void;
  /** 消えたあとに移る先（見積依頼書の画面）。一覧はその場で読み直すだけ */
  to?: string | undefined;
}) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const no = rfq.no ?? RFQ_MSG.draftNo;
  const close = () => !busy && onClose();
  const purge = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await api<void>("DELETE", `/rfqs/${rfq.id}`);
      // 一覧・見積書の「見積依頼 n件」・ホーム・{project}・回答の{vendorQuote}（依頼番号が外れる）を読み直す
      for (const k of [["rfqs"], ["estimate-rfqs"], ["home"], ["group"], ["vendor-quotes"], ["vendor-quote"]]) void qc.invalidateQueries({ queryKey: k });
      const text = RFQ_MSG.purged.replace("{no}", no);
      if (to) nav(to, { replace: true, state: { notice: text, noticeTestId: "rfq-purge-notice" } });
      else {
        notify({ text, testId: "rfq-purge-notice" });
        onClose();
      }
    } catch (e) {
      setBusy(false);
      setError(messageOf(e));
    }
  };
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="dlg" role="alertdialog" aria-label={RFQ_MSG.purge} onKeyDown={(e) => e.key === "Escape" && close()} data-testid="rfq-purge-dialog">
        <div className="dh">{RFQ_MSG.purge}</div>
        <div className="db">
          <p style={{ margin: 0 }} data-testid="rfq-purge-ask">{RFQ_MSG.purgeAsk.replace("{no}", no)}</p>
          <p className="hint" style={{ margin: "6px 0 0" }} data-testid="rfq-purge-note">{RFQ_MSG.purgeNote.replace("{no}", no)}</p>
          {rfq.answer ? (
            <p className="hint" style={{ margin: "4px 0 0" }} data-testid="rfq-purge-answer">{t(RFQ_MSG.purgeAnswer).replace("{no}", rfq.answer.quoteNo)}</p>
          ) : null}
          <ErrorText>{error}</ErrorText>
        </div>
        <div className="df">
          <button type="button" className="btn primary" autoFocus disabled={busy} onClick={close} data-testid="rfq-purge-cancel">取消</button>
          <button type="button" className="btn shu" disabled={busy} onClick={() => void purge()} data-testid="rfq-purge-confirm">{RFQ_MSG.purge}</button>
        </div>
      </div>
    </div>
  );
}
